/**
 * Seul fichier d’entrée SSO Sign.
 * Tuile / sso-verify : **Signature**
 */
import {
  getSignApiBase,
  getSuiteApiBase,
  getWhoamiUrl,
  isEmbeddedInHub,
  readUserId,
  SESSION_ERRORS,
  syncUserIdFromQuery,
} from "./suiteSession";

const APP_DISPLAY_NAME = "Aurélia Signature";
const SUITE_APP_TILE = "Signature";

function hideRoot(): void {
  document.getElementById("root")?.classList.add("hidden");
}

function showRoot(): void {
  document.getElementById("root")?.classList.remove("hidden");
}

function lockShell(): void {
  window.lockSuiteAppShell?.();
  hideRoot();
}

function unlockShell(): void {
  window.unlockSuiteAppShell?.();
  showRoot();
}

function showForbidden(): void {
  window.hideSuiteAuthModal?.();
  lockShell();
  window.showSuiteForbiddenModal?.({
    actions: [
      {
        label: "Actualiser",
        primary: true,
        onClick: () => {
          window.hideSuiteForbiddenModal?.();
          window.location.reload();
        },
      },
    ],
  });
  window.dispatchEvent(new CustomEvent("signAuthLost"));
}

function showNeedLogin(options?: { sessionLost?: boolean }): void {
  const sessionLost = options?.sessionLost === true;
  lockShell();
  window.hideSuiteForbiddenModal?.();
  window.showSuiteAuthModal?.(undefined, {
    autoReconnect: !sessionLost,
  });
  window.dispatchEvent(new CustomEvent("signAuthLost"));
}

async function fetchSsoVerify(userId: string): Promise<{ ok: boolean; forbidden?: boolean }> {
  const url = `${getSuiteApiBase()}/sso-verify.php?suite_user_id=${encodeURIComponent(userId)}&app=${encodeURIComponent(SUITE_APP_TILE)}`;
  try {
    const res = await fetch(url, { method: "GET", credentials: "include", cache: "no-store" });
    const data = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string };
    if (!res.ok || !data.success) {
      const err = data.error ?? "";
      const forbidden =
        res.status === 403 ||
        err === "app_not_permitted" ||
        err === "forbidden_app_permission" ||
        err === "admin_apps_enabled" ||
        err === "admin_apps_disabled";
      return { ok: false, forbidden };
    }
    return { ok: true };
  } catch {
    return { ok: false };
  }
}

async function fetchPing(userId: string): Promise<{ ok: boolean; forbidden?: boolean }> {
  try {
    const res = await fetch(`${getSignApiBase()}/ping.php`, {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      headers: { "X-User-Id": userId },
    });
    const data = (await res.json().catch(() => ({}))) as { ok?: boolean; success?: boolean; error?: string };
    const err = data.error ?? "";
    if (!res.ok || !(data.ok || data.success)) {
      const forbidden = res.status === 403 || err === "app_not_permitted" || err === "forbidden_app_permission";
      return { ok: false, forbidden };
    }
    return { ok: true };
  } catch {
    return { ok: false };
  }
}

async function verifyAndEnter(userId: string): Promise<boolean> {
  const sso = await fetchSsoVerify(userId);
  if (!sso.ok) {
    window.__signSessionVerified = false;
    if (sso.forbidden) {
      showForbidden();
      return false;
    }
    showNeedLogin({ sessionLost: true });
    return false;
  }
  const ping = await fetchPing(userId);
  if (!ping.ok) {
    window.__signSessionVerified = false;
    if (ping.forbidden) {
      showForbidden();
      return false;
    }
    showNeedLogin({ sessionLost: true });
    return false;
  }
  window.__signSessionVerified = true;
  unlockShell();
  window.hideSuiteAuthModal?.();
  window.hideSuiteForbiddenModal?.();
  window.startSuiteHubSessionWatch?.();
  window.dispatchEvent(new CustomEvent("signAuthReady", { detail: { userId: String(userId) } }));
  return true;
}

async function onAuthenticated(userIdArg?: number | string): Promise<void> {
  if (window.__aureliaOwnSite) {
    const userId = userIdArg != null ? String(userIdArg) : "";
    if (userId) await verifyAndEnter(userId);
    return;
  }
  const userId = userIdArg != null ? String(userIdArg) : readUserId();
  if (!userId) return;
  window.persistSuiteUserId?.(userId);
  await verifyAndEnter(userId);
}

function handleSessionLost(): void {
  if (window.__aureliaOwnSite) {
    window.__signSessionVerified = false;
    showNeedLogin({ sessionLost: true });
    return;
  }
  window.__signSessionVerified = false;
  window.stopSuiteIframeSsoPoll?.();
  window.stopSuiteHubSessionWatch?.();
  window.clearSuiteStoredIdentity?.();
  showNeedLogin({ sessionLost: true });
}

export function signNotifyApiAuthError(status: number, error?: string): boolean {
  const err = error ?? "";
  if (status === 403 && (err === "app_not_permitted" || err === "forbidden_app_permission")) {
    showForbidden();
    return true;
  }
  if (status === 401 && SESSION_ERRORS.has(err)) {
    handleSessionLost();
    return true;
  }
  return false;
}

function mountSharedModals(): void {
  window.suiteAuthModalMount?.({ appName: APP_DISPLAY_NAME });
  window.suiteForbiddenModalMount?.({ appName: APP_DISPLAY_NAME });
  window.installSuiteAppShell?.({ shellId: "signAppShell" });
  window.installSuiteClearCacheListener?.({ preserveKeys: [] });
}

function runSharedInit(): void {
  mountSharedModals();
  if (typeof window.installSuiteAuthPopup !== "function") {
    console.error("Sign : kit suite-shared introuvable");
    return;
  }
  window.installSuiteAuthPopup({
    redirect: "sign",
    isConnected: () => window.__signSessionVerified === true,
    onAuthenticated: (userId) => {
      void onAuthenticated(userId);
    },
    onSessionLost: () => {
      handleSessionLost();
    },
    patchFetch: false,
  });
  const whoamiUrl = getWhoamiUrl();
  if (!window.__aureliaOwnSite && whoamiUrl && typeof window.installSuiteHubSessionWatch === "function") {
    window.installSuiteHubSessionWatch({
      whoamiUrl,
      isEmbeddedInHub,
      hasStoredIdentity: () => Boolean(readUserId()),
      isAppSessionActive: () => window.__signSessionVerified === true,
      onSessionLost: () => {
        handleSessionLost();
      },
    });
  }
  void bootAuth();
}

async function bootAuth(): Promise<void> {
  if (window.__aureliaOwnSite) {
    lockShell();
    const userId = await window.aureliaHub2User?.();
    if (!userId) {
      showNeedLogin({ sessionLost: false });
      return;
    }
    await verifyAndEnter(userId);
    return;
  }
  syncUserIdFromQuery();
  lockShell();
  const delay = isEmbeddedInHub() ? 2600 : 800;
  await new Promise((r) => setTimeout(r, delay));
  const userId = readUserId();
  if (userId) {
    await verifyAndEnter(userId);
    return;
  }
  showNeedLogin({ sessionLost: false });
}

if (window.__SUITE_SHARED_READY__) {
  runSharedInit();
} else {
  document.addEventListener("suiteSharedReady", runSharedInit, { once: true });
  document.addEventListener("suiteSharedFailed", () => {
    console.error("Sign : échec chargement suite-shared");
  });
}
